import { useCallback, useEffect, useState } from 'react'
import { Building2, LockKeyhole, UserRound } from 'lucide-react'

import {
  getAgentMemory,
  updateAgentMemory,
  useGetAuthzMeQuery,
  type AgentMemoryScope
} from '@axilon-apps/domains-ontology-ui-state'

import { ChatMarkdown } from '../ChatMarkdown/ChatMarkdown'
import { Button } from '../ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Textarea } from '../ui/textarea'

/** Every mutation of the organization's document is admin-only on the proxy. */
const ORG_WRITE_PERMISSION = 'memory/org-write'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'forbidden'; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready' }

interface ScopeCopy {
  label: string
  blurb: string
  placeholder: string
}

const SCOPE_COPY: Record<AgentMemoryScope, ScopeCopy> = {
  org: {
    label: 'Org-wide',
    blurb:
      'Shared by everyone in your organization. Agents load this on every run, so keep it to conventions, equipment, and standing instructions that hold plant-wide.',
    placeholder: '## Conventions\n\n- PV = process value\n- SP = setpoint'
  },
  user: {
    label: 'Personal',
    blurb:
      'Private to you. Nobody else can read it, and agents only load it on your own runs — a good place for how you like answers written.',
    placeholder: '## Preferences\n\n- Answer with the number first, then the reasoning'
  }
}

function formatTimestamp(iso: string): string {
  if (!iso) return ''
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleString()
}

interface MemoryDoc {
  loadState: LoadState
  content: string
  dirty: boolean
  saving: boolean
  saveError: string | null
  justSaved: boolean
  lastModified: string
  setContent: (next: string) => void
  reload: () => void
  save: () => void
}

/**
 * One document's load/edit/save cycle. Held per scope so switching tabs keeps
 * unsaved edits on the other document alive.
 */
function useMemoryDoc(scope: AgentMemoryScope): MemoryDoc {
  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' })
  const [content, setContent] = useState('')
  const [savedContent, setSavedContent] = useState('')
  const [etag, setEtag] = useState('')
  const [lastModified, setLastModified] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  const load = useCallback(async () => {
    setLoadState({ kind: 'loading' })
    try {
      const result = await getAgentMemory(scope)
      if (!result.available) {
        setLoadState({ kind: 'unavailable' })
        return
      }
      if ('forbidden' in result) {
        setLoadState({ kind: 'forbidden', message: result.message })
        return
      }
      setContent(result.doc.content)
      setSavedContent(result.doc.content)
      setEtag(result.doc.etag)
      setLastModified(result.doc.lastModified)
      setLoadState({ kind: 'ready' })
    } catch (err) {
      setLoadState({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }, [scope])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(async () => {
    setSaving(true)
    setSaveError(null)
    setJustSaved(false)
    try {
      const result = await updateAgentMemory(scope, content, etag)
      switch (result.status) {
        case 'ok':
          setSavedContent(content)
          setEtag(result.doc.etag)
          setLastModified(result.doc.lastModified)
          setJustSaved(true)
          break
        case 'conflict':
          setEtag(result.currentEtag)
          setSaveError(
            'Someone else edited this document since you loaded it. Reload to get the latest version, then re-apply your changes.'
          )
          break
        case 'too_large':
          setSaveError('Document is too large — the limit is 256 KB.')
          break
        case 'forbidden':
          setSaveError(result.message)
          break
        case 'unavailable':
          setLoadState({ kind: 'unavailable' })
          break
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }, [scope, content, etag])

  return {
    loadState,
    content,
    dirty: content !== savedContent,
    saving,
    saveError,
    justSaved,
    lastModified,
    setContent,
    reload: () => void load(),
    save: () => void save()
  }
}

function MemoryDocPanel({
  doc,
  copy,
  readOnly,
  readOnlyNote
}: {
  doc: MemoryDoc
  copy: ScopeCopy
  readOnly: boolean
  readOnlyNote: string
}) {
  const { loadState } = doc

  if (loadState.kind === 'loading') {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  if (loadState.kind === 'unavailable') {
    return (
      <div className="rounded-md border border-st-warn/40 bg-st-warn-bg p-4 text-sm text-foreground">
        Agent memory isn’t configured for this environment (the{' '}
        <code className="font-mono">AGENT_MEMORY_BUCKET</code> is unset), so there’s nothing to edit
        yet.
      </div>
    )
  }

  if (loadState.kind === 'forbidden') {
    return (
      <div className="rounded-md border border-st-warn/40 bg-st-warn-bg p-4 text-sm text-foreground">
        {loadState.message}
      </div>
    )
  }

  if (loadState.kind === 'error') {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-st-down/40 bg-st-down-bg p-4 text-sm text-st-down">
        <span>Couldn’t load the document: {loadState.message}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={doc.reload}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <p className="shrink-0 text-xs text-muted-foreground">{copy.blurb}</p>
      {readOnly ? (
        <div className="flex shrink-0 items-center gap-2 rounded-md border border-st-warn/35 bg-st-warn-bg/70 px-2.5 py-1.5 text-11 text-foreground">
          <LockKeyhole className="size-3.5 shrink-0 text-st-warn" aria-hidden="true" />
          <span>{readOnlyNote}</span>
        </div>
      ) : null}
      {doc.saveError ? (
        <div className="shrink-0 rounded-md border border-st-down/40 bg-st-down-bg p-3 text-sm text-st-down">
          {doc.saveError}
        </div>
      ) : null}
      <Tabs defaultValue="source" className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <TabsList variant="line" className="self-start">
            <TabsTrigger value="source">{readOnly ? 'Source' : 'Edit'}</TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-3">
            {readOnly ? null : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={!doc.dirty || doc.saving}
                onClick={doc.save}
                className="border-brand/30 bg-brand-soft text-xs text-brand hover:bg-brand-soft/70 hover:text-brand"
              >
                {doc.saving ? 'Saving…' : 'Save'}
              </Button>
            )}
            <span className="max-w-60 truncate text-xs text-muted-foreground">
              {doc.justSaved && !doc.dirty
                ? 'Saved'
                : doc.dirty
                  ? 'Unsaved changes'
                  : doc.lastModified
                    ? `Last edited ${formatTimestamp(doc.lastModified)}`
                    : ''}
            </span>
          </div>
        </div>
        <TabsContent value="source" className="min-h-0 flex-1">
          {readOnly ? (
            <pre
              data-testid="memory-readonly-source"
              aria-label={`${copy.label} memory source`}
              aria-readonly="true"
              className="h-full min-h-64 w-full cursor-default overflow-auto rounded-md border border-border/60 bg-muted/35 p-3 font-mono text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground"
            >
              {doc.content}
            </pre>
          ) : (
            <Textarea
              value={doc.content}
              onChange={(event) => doc.setContent(event.target.value)}
              spellCheck={false}
              placeholder={copy.placeholder}
              className="h-full min-h-64 w-full resize-none overflow-y-auto font-mono text-sm leading-relaxed"
            />
          )}
        </TabsContent>
        <TabsContent value="preview" className="min-h-0 flex-1">
          <div className="h-full min-h-64 overflow-auto rounded-md border bg-muted/30 p-4 text-sm leading-6 text-foreground">
            {doc.content.trim() ? (
              <ChatMarkdown>{doc.content}</ChatMarkdown>
            ) : (
              <p className="text-muted-foreground">Nothing to preview yet.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

/**
 * Editor for the two always-injected `AGENTS.md` documents. Both hooks run on
 * mount so switching tabs is instant and each document keeps its own unsaved
 * edits.
 */
export function AgentMemoryEditor() {
  const org = useMemoryDoc('org')
  const personal = useMemoryDoc('user')
  const { data: authz } = useGetAuthzMeQuery()
  // Absent until authz resolves, which reads as no permission — the same answer
  // the proxy would give, and the tab flips to editable once it lands.
  const canEditOrg = authz?.permissions.includes(ORG_WRITE_PERMISSION) ?? false

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-344 flex-1 flex-col px-6 pt-0 pb-6 sm:px-8 lg:px-12">
      <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
        <Tabs defaultValue="org" className="flex min-h-0 flex-1 flex-col gap-2">
          <TabsList className="h-8 shrink-0 self-start rounded-full border border-border/70 bg-muted/25 p-0.5 shadow-none">
            <TabsTrigger
              value="org"
              className="h-7 flex-none gap-1.5 rounded-full px-3 text-xs shadow-none data-[state=active]:bg-background data-[state=active]:shadow-xs"
            >
              <Building2 className="size-3.5" aria-hidden="true" />
              {SCOPE_COPY.org.label}
            </TabsTrigger>
            <TabsTrigger
              value="user"
              className="h-7 flex-none gap-1.5 rounded-full px-3 text-xs shadow-none data-[state=active]:bg-background data-[state=active]:shadow-xs"
            >
              <UserRound className="size-3.5" aria-hidden="true" />
              {SCOPE_COPY.user.label}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="org" className="flex min-h-0 flex-1 flex-col">
            <MemoryDocPanel
              doc={org}
              copy={SCOPE_COPY.org}
              readOnly={!canEditOrg}
              readOnlyNote="Only an administrator can edit org-wide memory. Agents enforce the same restriction and cannot change it for non-admin users."
            />
          </TabsContent>
          <TabsContent value="user" className="flex min-h-0 flex-1 flex-col">
            <MemoryDocPanel
              doc={personal}
              copy={SCOPE_COPY.user}
              readOnly={false}
              readOnlyNote=""
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
